/**
 * primitives.js — geometric primitive types beyond line segments
 *
 * Adds to DCEL state:
 *   st.circles  { id, layerId, cx, cy, r, lineweight, linetype, color }
 *   st.arcs     { id, layerId, cx, cy, r, startAngle, endAngle, lineweight, linetype }
 *   st.splines  { id, layerId, pts: [{x,y}], closed, lineweight, linetype }
 *   st.polylines{ id, layerId, pts: [{x,y}], closed, filled, lineweight }
 *   st.regions  { id, layerId, boundary: [{type,data}], hatch }
 *
 * All coordinates in mm. All IDs via mkId().
 * Each primitive carries its own layer, lineweight, color — inheriting layer
 * defaults but overridable per-object.
 */

import { mkId } from "../utils/coords";
import { Geo }  from "../utils/geo";

// ─── Snap helpers for primitives ──────────────────────────────────────────────

/** Points on a circle useful for snapping: center, 4 quadrants */
export function circleSnapPoints(c) {
  return [
    { x: c.cx,       y: c.cy,       type: "center",    id: c.id },
    { x: c.cx + c.r, y: c.cy,       type: "quadrant",  id: c.id },
    { x: c.cx - c.r, y: c.cy,       type: "quadrant",  id: c.id },
    { x: c.cx,       y: c.cy + c.r, type: "quadrant",  id: c.id },
    { x: c.cx,       y: c.cy - c.r, type: "quadrant",  id: c.id },
  ];
}

/** Closest point on circle circumference to p */
export function snapToCircle(p, c) {
  const dx = p.x - c.cx, dy = p.y - c.cy;
  const d = Math.sqrt(dx*dx + dy*dy);
  if (d < 1e-9) return { x: c.cx + c.r, y: c.cy };
  return { x: c.cx + dx/d * c.r, y: c.cy + dy/d * c.r };
}

/** Closest point on arc to p (within arc angular range) */
export function snapToArc(p, a) {
  const dx = p.x - a.cx, dy = p.y - a.cy;
  let ang = Math.atan2(dy, dx);
  // Normalize to arc range
  let { startAngle: s, endAngle: e } = a;
  // Convert negative angles
  while (ang < s) ang += 2*Math.PI;
  if (ang <= e) {
    return { x: a.cx + Math.cos(ang)*a.r, y: a.cy + Math.sin(ang)*a.r };
  }
  // Return closest endpoint
  const ps = { x: a.cx + Math.cos(s)*a.r, y: a.cy + Math.sin(s)*a.r };
  const pe = { x: a.cx + Math.cos(e)*a.r, y: a.cy + Math.sin(e)*a.r };
  return Geo.dsq(p, ps) < Geo.dsq(p, pe) ? ps : pe;
}

/** Arc snap points: center, start, end, mid */
export function arcSnapPoints(a) {
  const mid = (a.startAngle + a.endAngle) / 2;
  return [
    { x: a.cx, y: a.cy, type: "center", id: a.id },
    { x: a.cx + Math.cos(a.startAngle)*a.r, y: a.cy + Math.sin(a.startAngle)*a.r, type: "endpoint", id: a.id },
    { x: a.cx + Math.cos(a.endAngle)*a.r,   y: a.cy + Math.sin(a.endAngle)*a.r,   type: "endpoint", id: a.id },
    { x: a.cx + Math.cos(mid)*a.r,           y: a.cy + Math.sin(mid)*a.r,           type: "midpoint", id: a.id },
  ];
}

// ─── Circle mutations ─────────────────────────────────────────────────────────

export function mutAddCircle(cx, cy, r, layerId, st) {
  if (r < 0.001) return st;
  const id = mkId("cir");
  const circle = { id, layerId: layerId || "walls", cx, cy, r };
  return { ...st, circles: { ...(st.circles || {}), [id]: circle } };
}

export function mutMoveCircle(id, cx, cy, st) {
  const c = (st.circles || {})[id]; if (!c) return st;
  return { ...st, circles: { ...st.circles, [id]: { ...c, cx, cy } } };
}

export function mutResizeCircle(id, r, st) {
  const c = (st.circles || {})[id]; if (!c || r < 0.001) return st;
  return { ...st, circles: { ...st.circles, [id]: { ...c, r } } };
}

export function mutDeleteCircle(id, st) {
  const cs = { ...(st.circles || {}) }; delete cs[id];
  return { ...st, circles: cs };
}

export function mutUpdateCircle(id, props, st) {
  const c = (st.circles || {})[id]; if (!c) return st;
  return { ...st, circles: { ...st.circles, [id]: { ...c, ...props } } };
}

// ─── Arc mutations ────────────────────────────────────────────────────────────

/** startAngle, endAngle in radians (0 = right, CCW positive) */
export function mutAddArc(cx, cy, r, startAngle, endAngle, layerId, st) {
  if (r < 0.001) return st;
  const id = mkId("arc");
  const arc = { id, layerId: layerId || "walls", cx, cy, r, startAngle, endAngle };
  return { ...st, arcs: { ...(st.arcs || {}), [id]: arc } };
}

/** Create arc from 3 points (start, through, end) */
export function mutAddArc3Pt(p1, p2, p3, layerId, st) {
  const c = circumcenter(p1, p2, p3);
  if (!c) return st;
  const r = Geo.d(c, p1);
  const a1 = Math.atan2(p1.y - c.y, p1.x - c.x);
  const a3 = Math.atan2(p3.y - c.y, p3.x - c.x);
  return mutAddArc(c.x, c.y, r, a1, a3, layerId, st);
}

export function mutMoveArc(id, cx, cy, st) {
  const a = (st.arcs || {})[id]; if (!a) return st;
  return { ...st, arcs: { ...st.arcs, [id]: { ...a, cx, cy } } };
}

export function mutDeleteArc(id, st) {
  const as = { ...(st.arcs || {}) }; delete as[id];
  return { ...st, arcs: as };
}

export function mutUpdateArc(id, props, st) {
  const a = (st.arcs || {})[id]; if (!a) return st;
  return { ...st, arcs: { ...st.arcs, [id]: { ...a, ...props } } };
}

// ─── Spline mutations ─────────────────────────────────────────────────────────

export function mutAddSpline(pts, closed, layerId, st) {
  if (pts.length < 2) return st;
  const id = mkId("spl");
  const spline = { id, layerId: layerId || "walls", pts: [...pts], closed: !!closed };
  return { ...st, splines: { ...(st.splines || {}), [id]: spline } };
}

export function mutAddSplinePt(id, pt, st) {
  const s = (st.splines || {})[id]; if (!s) return st;
  return { ...st, splines: { ...st.splines, [id]: { ...s, pts: [...s.pts, pt] } } };
}

export function mutUpdateSplinePt(id, idx, pt, st) {
  const s = (st.splines || {})[id]; if (!s || idx >= s.pts.length) return st;
  const pts = [...s.pts]; pts[idx] = pt;
  return { ...st, splines: { ...st.splines, [id]: { ...s, pts } } };
}

export function mutDeleteSpline(id, st) {
  const ss = { ...(st.splines || {}) }; delete ss[id];
  return { ...st, splines: ss };
}

export function mutCloseSpline(id, st) {
  const s = (st.splines || {})[id]; if (!s) return st;
  return { ...st, splines: { ...st.splines, [id]: { ...s, closed: true } } };
}

// ─── Polyline mutations ───────────────────────────────────────────────────────

export function mutAddPolyline(pts, closed, filled, layerId, st) {
  if (pts.length < 2) return st;
  const id = mkId("pl");
  const poly = { id, layerId: layerId || "walls", pts: [...pts], closed: !!closed, filled: !!filled };
  return { ...st, polylines: { ...(st.polylines || {}), [id]: poly } };
}

export function mutDeletePolyline(id, st) {
  const ps = { ...(st.polylines || {}) }; delete ps[id];
  return { ...st, polylines: ps };
}

export function mutUpdatePolyline(id, props, st) {
  const p = (st.polylines || {})[id]; if (!p) return st;
  return { ...st, polylines: { ...st.polylines, [id]: { ...p, ...props } } };
}

// ─── Hatch / fill ─────────────────────────────────────────────────────────────

export const HATCH_PATTERNS = {
  none:       { id: "none",       label: "None",          angle: 0,   spacing: 0  },
  ansi31:     { id: "ansi31",     label: "General (45°)", angle: 45,  spacing: 4  },
  ansi32:     { id: "ansi32",     label: "Steel",         angle: 45,  spacing: 4, double: true },
  wood:       { id: "wood",       label: "Wood grain",    angle: 0,   spacing: 3  },
  concrete:   { id: "concrete",   label: "Concrete",      angle: 45,  spacing: 5, dots: true  },
  brick:      { id: "brick",      label: "Brick",         angle: 0,   spacing: 6, type: "brick" },
  glass:      { id: "glass",      label: "Glass",         angle: 45,  spacing: 2, double: true },
  insulation: { id: "insulation", label: "Insulation",    angle: 0,   spacing: 8, type: "zigzag" },
  earth:      { id: "earth",      label: "Earth / Fill",  angle: 45,  spacing: 3, dots: true  },
  metal:      { id: "metal",      label: "Metal (section)",angle: 45, spacing: 3  },
  plywood:    { id: "plywood",    label: "Plywood",       angle: 0,   spacing: 3, type: "plywood" },
};

export function mutSetHatch(entityId, entityType, patternId, scale, angle, st) {
  const key = `${entityType}Hatches`;
  return {
    ...st,
    [key]: {
      ...(st[key] || {}),
      [entityId]: { patternId, scale: scale || 1, angle: angle || 0 },
    },
  };
}

// ─── Geometry helpers ─────────────────────────────────────────────────────────

/** Circumcenter of 3 points — used for 3-point arc */
function circumcenter(p1, p2, p3) {
  const ax = p2.x - p1.x, ay = p2.y - p1.y;
  const bx = p3.x - p1.x, by = p3.y - p1.y;
  const D  = 2 * (ax * by - ay * bx);
  if (Math.abs(D) < 1e-9) return null; // collinear
  const ux = (by * (ax*ax + ay*ay) - ay * (bx*bx + by*by)) / D;
  const uy = (ax * (bx*bx + by*by) - bx * (ax*ax + ay*ay)) / D;
  return { x: p1.x + ux, y: p1.y + uy };
}

/** Catmull-Rom spline points → SVG path data */
export function splineToPath(pts, closed, tension = 0.5) {
  if (pts.length < 2) return "";
  const p = closed ? [...pts, pts[0], pts[1]] : pts;
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < (closed ? pts.length : pts.length - 1); i++) {
    const p0 = p[Math.max(0, i-1)];
    const p1 = p[i];
    const p2 = p[i+1] || p[i];
    const p3 = p[i+2] || p2;
    const cp1x = p1.x + (p2.x - p0.x) * tension / 3;
    const cp1y = p1.y + (p2.y - p0.y) * tension / 3;
    const cp2x = p2.x - (p3.x - p1.x) * tension / 3;
    const cp2y = p2.y - (p3.y - p1.y) * tension / 3;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  if (closed) d += " Z";
  return d;
}

/** Bounding box of a circle */
export function circleBBox(c) {
  return { minX: c.cx-c.r, maxX: c.cx+c.r, minY: c.cy-c.r, maxY: c.cy+c.r };
}
